import type { SandalModel, SandalUpperStrapColor } from './prescriptionState';

/**
 * Upper strap colours per sandal model, in one place.
 *
 * Every model starts from the six base colours. Two lists adjust that, and
 * both are THE single source for everything downstream:
 *   - UPPER_STRAP_DARK_BLUE_MODELS: the models that also take Dark blue,
 *     appended LAST so the models that offer it show the identical list and
 *     a podiatrist switching between them keeps their place in it;
 *   - UPPER_STRAP_EXCLUDED_BY_MODEL: base colours a model does NOT take
 *     (Velcro: Sand beige and Mustard yellow, since 2026-11-03).
 * The dropdown (SandalStyleSection), the model-switch clearing rule, the
 * legacy marker on a line saved before a colour was withdrawn and the paper
 * form's "(Buckle / Velcro)" style suffixes all derive from these two lists,
 * so a model gaining or losing a colour is one entry here and nothing else.
 * The values themselves never change: every colour stays in the stored
 * enum, the lab sheet, the patient page whitelist (get_patient_public_view)
 * and the locales, because older lines still carry them.
 */

export interface StrapColourOption {
  value: SandalUpperStrapColor;
  label: string;
}

/** A dropdown entry: an offered colour, or the line's saved colour that its
 *  model no longer offers (shown, disabled and named, never swapped). */
export interface StrapColourChoice extends StrapColourOption {
  legacy?: boolean;
}

/** The labels the podiatrist reads on the model cards and the paper form. */
export const SANDAL_MODEL_LABELS: Readonly<Record<SandalModel, string>> = {
  buckle: 'Buckle',
  velcro: 'Velcro',
  flipflop: 'Flipflop',
};

/** Every model, in card order. */
export const SANDAL_MODELS: ReadonlyArray<SandalModel> = ['buckle', 'velcro', 'flipflop'];

/** The six base colours, in dropdown order. */
export const UPPER_STRAP_COLOR_BASE: ReadonlyArray<StrapColourOption> = [
  { value: 'black', label: 'Black' },
  { value: 'white', label: 'White' },
  { value: 'brown', label: 'Brown' },
  { value: 'military_green', label: 'Military green' },
  { value: 'sand_beige', label: 'Sand beige' },
  { value: 'mustard_yellow', label: 'Mustard yellow' },
];

export const UPPER_STRAP_DARK_BLUE: StrapColourOption = { value: 'dark_blue', label: 'Dark blue' };

/** Every colour any model can take, base first then Dark blue: the paper
 *  form's tick row and the label source for older lines. */
export const UPPER_STRAP_ALL_COLOURS: ReadonlyArray<StrapColourOption> = [
  ...UPPER_STRAP_COLOR_BASE,
  UPPER_STRAP_DARK_BLUE,
];

/**
 * THE single source for which models take a Dark blue upper strap. Velcro
 * since R36 (2026-06-10); Buckle since 2026-09-25, when Perpedes confirmed
 * they build it (Rafael). Flipflop is deliberately absent: the lab does not
 * make that upper in Dark blue.
 */
export const UPPER_STRAP_DARK_BLUE_MODELS: ReadonlyArray<SandalModel> = ['buckle', 'velcro'];

/**
 * THE single source for base colours a model does NOT take. Velcro lost Sand
 * beige and Mustard yellow on 2026-11-03 (Rafael): that upper is no longer
 * made in those colours. Buckle and Flipflop keep every base colour. A model
 * absent here takes all six.
 */
export const UPPER_STRAP_EXCLUDED_BY_MODEL: Readonly<
  Partial<Record<SandalModel, ReadonlyArray<SandalUpperStrapColor>>>
> = {
  velcro: ['sand_beige', 'mustard_yellow'],
};

/** The dropdown's options for a model (the base six until a model is picked). */
export function upperStrapOptionsFor(model: SandalModel | undefined): ReadonlyArray<StrapColourOption> {
  if (!model) return UPPER_STRAP_COLOR_BASE;
  const excluded = UPPER_STRAP_EXCLUDED_BY_MODEL[model] ?? [];
  const base = UPPER_STRAP_COLOR_BASE.filter((o) => !excluded.includes(o.value));
  return UPPER_STRAP_DARK_BLUE_MODELS.includes(model) ? [...base, UPPER_STRAP_DARK_BLUE] : base;
}

/** True when `model` offers `colour`; an unset colour is never a conflict. */
export function modelOffersUpperStrap(
  model: SandalModel | undefined,
  colour: SandalUpperStrapColor | undefined,
): boolean {
  if (!colour) return true;
  return upperStrapOptionsFor(model).some((o) => o.value === colour);
}

/** The label of any colour, offered or not (an older line may carry one its
 *  model no longer takes). */
export function upperStrapLabel(colour: SandalUpperStrapColor): string {
  return UPPER_STRAP_ALL_COLOURS.find((o) => o.value === colour)?.label ?? colour.replace(/_/g, ' ');
}

/**
 * What the dropdown renders for a line: the model's options, plus the line's
 * saved colour as a DISABLED, named entry when the model no longer offers it
 * (a Velcro line prescribed with Sand beige before 2026-11-03). The saved
 * value stays selected and legible, is never replaced by another colour, and
 * cannot be picked again once the podiatrist moves off it: refuse, do not
 * substitute.
 */
export function upperStrapChoicesFor(
  model: SandalModel | undefined,
  saved: SandalUpperStrapColor | undefined,
): ReadonlyArray<StrapColourChoice> {
  const options = upperStrapOptionsFor(model);
  if (!saved || options.some((o) => o.value === saved)) return options;
  const label = model
    ? `${upperStrapLabel(saved)} (no longer offered for ${SANDAL_MODEL_LABELS[model]})`
    : upperStrapLabel(saved);
  return [...options, { value: saved, label, legacy: true }];
}

/** The models that offer a colour, in card order. */
export function modelsOfferingUpperStrap(colour: SandalUpperStrapColor): ReadonlyArray<SandalModel> {
  return SANDAL_MODELS.filter((m) => modelOffersUpperStrap(m, colour));
}

/** "(Buckle / Flipflop)" for a colour not every model takes; undefined when
 *  every model does, so the paper form prints the bare label. */
export function upperStrapModelsSuffix(colour: SandalUpperStrapColor): string | undefined {
  const models = modelsOfferingUpperStrap(colour);
  if (models.length === SANDAL_MODELS.length) return undefined;
  if (models.length === 0) return '(not offered)';
  return `(${models.map((m) => SANDAL_MODEL_LABELS[m]).join(' / ')})`;
}

/** The paper form's per-colour suffixes, derived from the two lists, so paper
 *  and app cannot disagree on which models take a colour. */
export function upperStrapSuffixByValue(): Readonly<Record<string, string>> {
  const out: Record<string, string> = {};
  for (const o of UPPER_STRAP_ALL_COLOURS) {
    const suffix = upperStrapModelsSuffix(o.value);
    if (suffix) out[o.value] = suffix;
  }
  return out;
}

/** "Buckle / Velcro": the models that take Dark blue. */
export function darkBlueModelsLabel(): string {
  return modelsOfferingUpperStrap('dark_blue')
    .map((m) => SANDAL_MODEL_LABELS[m])
    .join(' / ');
}
